import type { Look } from "./catalog";

export const MAX_GRID = 14; // the biggest room size; tile indexes and room art are sized for it
export const STEP_MS = 280; // server moves avatars one tile per step
export const MAX_CHAT = 120;

// ---------- walls and floors ----------

/** The three looks of the old pick-a-theme rooms. Each one is now a wall and a floor sold separately. */
export type ThemeId = "lounge" | "rooftop" | "arcade";
export const THEMES: ThemeId[] = ["lounge", "rooftop", "arcade"];

export type WallPattern = "plain" | "dots" | "brick" | "lines" | "grid" | "stripes" | "tile" | "panel";

export interface WallDef {
  id: string;
  name: string;
  price: number; // in Bits; 0 = everyone owns it
  color: string;
  side: string; // the left wall, a shade darker
  pattern: WallPattern;
}

export interface FloorDef {
  id: string;
  name: string;
  price: number;
  a: string; // checker colors
  b: string;
}

export const WALLS: WallDef[] = [
  { id: "plain", name: "Plain White", price: 0, color: "#e8e8ee", side: "#d4d4dc", pattern: "plain" },
  { id: "lounge", name: "Bubblegum", price: 200, color: "#f4d3e2", side: "#e6bdd1", pattern: "dots" },
  { id: "lime", name: "Lime Stripe", price: 250, color: "#c8f066", side: "#b2da50", pattern: "stripes" },
  { id: "rooftop", name: "Sunset Brick", price: 250, color: "#f4a76b", side: "#e48d55", pattern: "brick" },
  { id: "arcade", name: "Neon Arcade", price: 300, color: "#3b2f63", side: "#30264f", pattern: "lines" },
  { id: "chrome", name: "Chrome Grid", price: 350, color: "#c4c8d4", side: "#a9aebc", pattern: "grid" },
  { id: "subway", name: "Subway Tile", price: 250, color: "#eef0f0", side: "#d8dcdc", pattern: "tile" },
  { id: "panel", name: "Concrete Panels", price: 300, color: "#9ea0a8", side: "#8a8c94", pattern: "panel" },
  { id: "blackout", name: "Blackout", price: 300, color: "#2a2a32", side: "#212128", pattern: "stripes" },
];

export const FLOORS: FloorDef[] = [
  { id: "concrete", name: "Bare Concrete", price: 0, a: "#b4b4bc", b: "#acacb4" },
  { id: "lounge", name: "Honey Wood", price: 150, a: "#d6a77a", b: "#c99a6e" },
  { id: "rooftop", name: "Rooftop Tiles", price: 150, a: "#8d9aa6", b: "#808d99" },
  { id: "arcade", name: "Arcade Carpet", price: 200, a: "#2a2340", b: "#241e38" },
  { id: "checker", name: "Checkerboard", price: 250, a: "#ececf2", b: "#2a2a32" },
  { id: "rubber", name: "Black Rubber", price: 150, a: "#2e2e34", b: "#292930" },
  { id: "plywood", name: "Skate Plywood", price: 200, a: "#dcb888", b: "#d2ac7a" },
  { id: "acid", name: "Acid Check", price: 300, a: "#c6ff00", b: "#1e1e24" },
];

export const WALL_BY_ID: Record<string, WallDef> = Object.fromEntries(WALLS.map((w) => [w.id, w]));
export const FLOOR_BY_ID: Record<string, FloorDef> = Object.fromEntries(FLOORS.map((f) => [f.id, f]));
export const DEFAULT_WALL = "plain";
export const DEFAULT_FLOOR = "concrete";

/**
 * Room sizes a player can expand to, smallest first. Every new room starts at the first one.
 * A bigger room holds more furniture (`pieces`, wall pieces included) and more people at once (`people`).
 */
export const ROOM_SIZES = [
  { size: 6, price: 0, pieces: 15, people: 6 },
  { size: 8, price: 400, pieces: 25, people: 10 },
  { size: 10, price: 900, pieces: 40, people: 15 },
  { size: 12, price: 1600, pieces: 60, people: 20 },
  { size: 14, price: 2500, pieces: 80, people: 25 },
];

/** The limits of a room size (the biggest size that fits, for sizes not on the list). */
export function limitsOf(size: number) {
  return [...ROOM_SIZES].reverse().find((s) => s.size <= size) ?? ROOM_SIZES[0];
}

export const doorOf = (size: number) => ({ x: size >> 1, y: size - 1 }); // spawn tile, front edge

// ---------- room cards ----------

/** Backgrounds for a room's card (the picture on X posts). All free. */
export interface CardBg {
  id: string;
  name: string;
  top: string; // gradient, top to bottom
  bottom: string;
  logo: string; // "XROOMS" and the tap-to-join pill
  text: string; // the room name
  shadow: string; // under the text
  pillText: string;
}

export const CARD_BGS: CardBg[] = [
  { id: "purple", name: "Night Purple", top: "#34245f", bottom: "#160f2c", logo: "#ff5fa2", text: "#ffffff", shadow: "#110c20", pillText: "#ffffff" },
  { id: "black", name: "X Black", top: "#1c1c20", bottom: "#050506", logo: "#c6ff00", text: "#ffffff", shadow: "#000000", pillText: "#000000" },
  { id: "lime", name: "Acid Lime", top: "#d4ff3a", bottom: "#86c400", logo: "#111111", text: "#111111", shadow: "#f4ffc8", pillText: "#c6ff00" },
  { id: "pink", name: "Bubblegum", top: "#ff7fbf", bottom: "#c2185b", logo: "#ffffff", text: "#ffffff", shadow: "#5a0a2c", pillText: "#c2185b" },
  { id: "sky", name: "Sky", top: "#8fe0ff", bottom: "#2b7bd6", logo: "#ffffff", text: "#ffffff", shadow: "#0d3466", pillText: "#2b7bd6" },
  { id: "sunset", name: "Sunset", top: "#ffc23d", bottom: "#ff3d6e", logo: "#2a0f3a", text: "#ffffff", shadow: "#5a1030", pillText: "#ffffff" },
  { id: "chrome", name: "Chrome", top: "#f4f4f6", bottom: "#8a8a94", logo: "#111111", text: "#111111", shadow: "#ffffff", pillText: "#c6ff00" },
  { id: "forest", name: "Forest", top: "#2f6b4a", bottom: "#0e2418", logo: "#b4ff39", text: "#ffffff", shadow: "#06130c", pillText: "#0e2418" },
];
export const CARD_BG_BY_ID: Record<string, CardBg> = Object.fromEntries(CARD_BGS.map((b) => [b.id, b]));
export const DEFAULT_CARD_BG = "purple";

/** Who can come into a room: everyone, the owner's Crew only, or the Crew plus whoever the owner lets in when they knock. */
export type Access = "open" | "crew" | "knock";
export const ACCESS: Access[] = ["open", "crew", "knock"];

// ---------- furniture ----------

export type FurniKind = "mattress" | "sofa" | "plant" | "table" | "lamp" | "cabinet" | "rug" | "crate" | "neon" | "tv" | "beanbag" | "lavalamp"
  | "djbooth" | "speaker" | "vending" | "rack" | "shoeboxes"
  | "bed" | "checkbed" | "bunkbed" | "nightstand" | "dresser" | "wardrobe" | "desk" | "deskchair" | "checkrug"
  | "chromebed" | "canopybed" | "vanity" | "bubblechair" | "floormirror" | "shagrug"
  | "sunflowers" | "tulips" | "roses" | "planter" | "blossom"
  | "waterbed" | "futon" | "papasan" | "gamestand" | "hifi" | "tapeshelf" | "fishtank" | "plasma" | "memphisrug"
  // hung on a wall
  | "starposter" | "heartposter" | "stripeposter" | "frame" | "miniframe" | "vinyl" | "window" | "sunset" | "neonsign"
  | "skatewall" | "sneakershelf" | "graffiti" | "stringlights" | "butterflies"
  | "vines" | "neonrose"
  | "glowstars" | "smileyposter" | "beadcurtain" | "cdrack";

/** A piece as the renderer sees it: kind, tile footprint and color. */
export interface Furni {
  kind: FurniKind;
  x: number;
  y: number;
  w: number; // footprint in tiles
  h: number;
  color: string;
  solid: boolean;
  wall?: 0 | 1; // hung on the back wall (0, along x) or the left wall (1, along y)
  dir?: Turn; // which way a floor piece's front faces: 0 +y, 1 +x, 2 -y, 3 -x (2 and 3 show its back)
}

export interface FurniDef {
  id: string;
  name: string;
  price: number; // in Bits
  kind: FurniKind;
  w: number;
  h: number;
  color: string;
  solid: boolean; // false for flat pieces (rugs, floor neon) you can walk over
  wall: boolean; // hangs on a wall; w is its width along the wall
}

export const WALL_KINDS = new Set<FurniKind>(["starposter", "heartposter", "stripeposter", "frame", "miniframe", "vinyl", "window", "sunset", "neonsign", "skatewall", "sneakershelf", "graffiti", "stringlights", "butterflies", "vines", "neonrose", "glowstars", "smileyposter", "beadcurtain", "cdrack"]);
/** Flower pieces, floor and wall, shown together in the Shop. */
export const FLOWER_KINDS = new Set<FurniKind>(["sunflowers", "tulips", "roses", "planter", "blossom", "vines", "neonrose"]);
/** Pieces that lie flat on the floor: you walk over them and they draw under everything else. */
export const FLAT_KINDS = new Set<FurniKind>(["rug", "neon", "checkrug", "shagrug", "memphisrug"]);

const fd = (id: string, name: string, price: number, kind: FurniKind, w: number, h: number, color: string): FurniDef =>
  ({ id, name, price, kind, w, h, color, solid: !FLAT_KINDS.has(kind) && !WALL_KINDS.has(kind), wall: WALL_KINDS.has(kind) });

export const FURNI: FurniDef[] = [
  fd("mattress", "Mattress", 60, "mattress", 2, 1, "#f0f0f4"),
  fd("plant_fern", "Fern", 40, "plant", 1, 1, "#3c9a5f"),
  fd("plant_lime", "Lime Plant", 40, "plant", 1, 1, "#4caf50"),
  fd("lamp_warm", "Floor Lamp", 60, "lamp", 1, 1, "#ffd98a"),
  fd("lamp_pink", "Pink Lamp", 60, "lamp", 1, 1, "#ff9ad5"),
  fd("beanbag_pink", "Pink Beanbag", 80, "beanbag", 1, 1, "#ff6fae"),
  fd("beanbag_blue", "Blue Beanbag", 80, "beanbag", 1, 1, "#7ad8ff"),
  fd("beanbag_lilac", "Lilac Beanbag", 80, "beanbag", 1, 1, "#c9a0ff"),
  fd("lava_pink", "Pink Lava Lamp", 90, "lavalamp", 1, 1, "#ff5fa2"),
  fd("lava_lime", "Lime Lava Lamp", 90, "lavalamp", 1, 1, "#b4ff39"),
  fd("crate_wood", "Crate", 70, "crate", 2, 1, "#b07a45"),
  fd("table_coffee", "Coffee Table", 120, "table", 2, 1, "#6b4a2b"),
  fd("table_patio", "Patio Table", 150, "table", 2, 2, "#e8e8e8"),
  fd("rug_lilac", "Small Rug", 60, "rug", 2, 2, "#8a5ad8"),
  fd("rug_red", "Red Rug", 100, "rug", 4, 3, "#8e3b46"),
  fd("rug_grey", "Big Grey Rug", 220, "rug", 6, 6, "#5f6b78"),
  fd("loveseat_cyan", "Cyan Loveseat", 180, "sofa", 2, 1, "#21d4fd"),
  fd("sofa_blue", "Blue Sofa", 250, "sofa", 3, 1, "#3f6fa8"),
  fd("sofa_pink", "Pink Sofa", 250, "sofa", 3, 1, "#f39cbc"),
  fd("sofa_black", "Black Sofa", 250, "sofa", 3, 1, "#2b2b35"),
  fd("tv_retro", "Retro TV", 200, "tv", 1, 1, "#d8d4e0"),
  fd("neon_pink", "Floor Neon", 300, "neon", 4, 4, "#ff3d8b"),
  fd("cabinet_pink", "Pink Arcade", 350, "cabinet", 1, 1, "#ff3d8b"),
  fd("cabinet_cyan", "Cyan Arcade", 350, "cabinet", 1, 1, "#21d4fd"),
  fd("cabinet_lime", "Lime Arcade", 350, "cabinet", 1, 1, "#b4ff39"),
  fd("cabinet_gold", "Gold Arcade", 350, "cabinet", 1, 1, "#ffb321"),
  fd("cabinet_purple", "Purple Arcade", 350, "cabinet", 1, 1, "#c04bff"),
  fd("shoeboxes", "Shoebox Stack", 90, "shoeboxes", 1, 1, "#ff7a1a"),
  fd("speaker_black", "Speaker Stack", 160, "speaker", 1, 1, "#26262e"),
  fd("rack_chrome", "Clothing Rack", 220, "rack", 2, 1, "#c4c8d4"),
  fd("vending_lime", "Soda Machine", 380, "vending", 1, 1, "#c6ff00"),
  fd("vending_red", "Red Soda Machine", 380, "vending", 1, 1, "#d0283a"),
  fd("djbooth", "DJ Booth", 450, "djbooth", 2, 1, "#1e1e26"),
  // bedroom
  fd("bed_blackout", "Blackout Bed", 320, "bed", 2, 3, "#454552"),
  fd("bed_checker", "Checker Bed", 380, "checkbed", 2, 3, "#f4f4f6"),
  fd("bed_bunk", "Bunk Bed", 420, "bunkbed", 1, 2, "#26262e"),
  fd("nightstand", "Nightstand", 90, "nightstand", 1, 1, "#3a3a46"),
  fd("dresser", "Dresser", 180, "dresser", 2, 1, "#e8e8ee"),
  fd("wardrobe", "Wardrobe", 260, "wardrobe", 2, 1, "#4a4a58"),
  fd("wardrobe_pink", "Pink Wardrobe", 260, "wardrobe", 2, 1, "#ff8fc0"),
  fd("desk", "Computer Desk", 240, "desk", 2, 1, "#d8d8e0"),
  fd("deskchair", "Desk Chair", 140, "deskchair", 1, 1, "#26262e"),
  fd("rug_checker", "Checker Rug", 150, "checkrug", 3, 2, "#1e1e24"),
  fd("bed_chrome", "Chrome Bed", 340, "chromebed", 2, 3, "#ff8fc0"),
  fd("bed_canopy", "Canopy Bed", 480, "canopybed", 2, 3, "#c9a0ff"),
  fd("vanity", "Vanity", 280, "vanity", 2, 1, "#f4f4f6"),
  fd("bubblechair", "Bubble Chair", 160, "bubblechair", 1, 1, "#ff5fa2"),
  fd("floormirror", "Floor Mirror", 120, "floormirror", 1, 1, "#c4c8d4"),
  fd("rug_shag", "Shag Rug", 130, "shagrug", 2, 2, "#c9a0ff"),
  // 90s bedroom
  fd("waterbed_memphis", "Memphis Waterbed", 400, "waterbed", 2, 3, "#2fb8a8"),
  fd("waterbed_plaid", "Flannel Waterbed", 400, "waterbed", 2, 3, "#b8283a"),
  fd("waterbed_stars", "Starry Waterbed", 400, "waterbed", 2, 3, "#24306a"),
  fd("futon_navy", "Futon", 220, "futon", 2, 1, "#2a3a6a"),
  fd("futon_plum", "Plum Futon", 220, "futon", 2, 1, "#6a2a5a"),
  fd("papasan_teal", "Papasan Chair", 150, "papasan", 1, 1, "#2fb8a8"),
  fd("papasan_purple", "Purple Papasan", 150, "papasan", 1, 1, "#8a5ad8"),
  fd("gamestand", "Game Console Stand", 320, "gamestand", 2, 1, "#2a2a30"),
  fd("hifi", "Stereo Tower", 210, "hifi", 1, 1, "#26262e"),
  fd("tapeshelf", "Tape Shelf", 130, "tapeshelf", 1, 1, "#e8e8ee"),
  fd("fishtank", "Fish Tank", 280, "fishtank", 2, 1, "#5ab8e0"),
  fd("plasma", "Plasma Ball", 160, "plasma", 1, 1, "#c04bff"),
  fd("rug_memphis", "Memphis Rug", 140, "memphisrug", 3, 2, "#1e1e24"),
  // flowers
  fd("flowers_sun", "Sunflowers", 70, "sunflowers", 1, 1, "#ffc21a"),
  fd("tulips_pink", "Pink Tulips", 50, "tulips", 1, 1, "#ff5fa2"),
  fd("tulips_white", "White Tulips", 50, "tulips", 1, 1, "#f4f4f6"),
  fd("roses_black", "Black Roses", 90, "roses", 1, 1, "#4a2a3c"),
  fd("roses_red", "Red Roses", 90, "roses", 1, 1, "#d0283a"),
  fd("planter", "Flower Planter", 160, "planter", 2, 1, "#9a9aa6"),
  fd("blossom", "Blossom Tree", 240, "blossom", 1, 1, "#ffb3d1"),
  // wall pieces
  fd("miniframe_photo", "Mini Frame", 50, "miniframe", 1, 1, "#2b2b35"),
  fd("poster_star", "Star Poster", 60, "starposter", 2, 1, "#ff7fbf"),
  fd("poster_heart", "Heart Poster", 60, "heartposter", 2, 1, "#7ec8ff"),
  fd("poster_stripes", "Neon Stripes Poster", 90, "stripeposter", 2, 1, "#2a2048"),
  fd("vinyl_black", "Vinyl Record", 120, "vinyl", 1, 1, "#ff3d8b"),
  fd("window_cloud", "Cloud Window", 150, "window", 3, 1, "#f8f4ff"),
  fd("window_sunset", "Sunset Window", 180, "sunset", 3, 1, "#ffffff"),
  fd("frame_gold", "Gold Frame", 200, "frame", 2, 1, "#e8b84a"),
  fd("frame_chrome", "Chrome Frame", 200, "frame", 2, 1, "#c4c8d4"),
  fd("sign_neon", "Neon Sign", 300, "neonsign", 4, 1, "#21d4fd"),
  fd("wall_skate", "Skate Decks", 180, "skatewall", 2, 1, "#ff5fa2"),
  fd("wall_sneakers", "Sneaker Shelf", 260, "sneakershelf", 3, 1, "#e8e8ee"),
  fd("wall_graffiti", "Graffiti Tag", 320, "graffiti", 4, 1, "#c6ff00"),
  fd("wall_lights", "String Lights", 120, "stringlights", 3, 1, "#ffd98a"),
  fd("wall_butterflies", "Butterflies", 90, "butterflies", 2, 1, "#c4c8d4"),
  fd("wall_vines", "Flower Vines", 110, "vines", 3, 1, "#3f8a4a"),
  fd("wall_neonrose", "Neon Rose", 180, "neonrose", 1, 1, "#ff3d8b"),
  fd("wall_glowstars", "Glow Stars", 80, "glowstars", 3, 1, "#d8ff9a"),
  fd("poster_smiley", "Smiley Poster", 70, "smileyposter", 2, 1, "#ffd23f"),
  fd("wall_beads", "Bead Curtain", 140, "beadcurtain", 1, 1, "#c8955a"),
  fd("wall_cdrack", "CD Rack", 100, "cdrack", 1, 1, "#26262e"),
];

export const FURNI_BY_ID: Record<string, FurniDef> = Object.fromEntries(FURNI.map((f) => [f.id, f]));

/**
 * How far a piece is turned, a quarter turn at a time. Odd turns swap w and h. Wall pieces only use 0 and 1,
 * which pick the back or the left wall.
 */
export type Turn = 0 | 1 | 2 | 3;
export const asTurn = (v: unknown, wall = false): Turn => (v === 1 || v === 2 || v === 3 ? (wall ? ((v & 1) as Turn) : v) : 0);

/** A piece placed in a room. `u` is unique within the room. */
export interface Placed {
  u: number;
  id: string;
  x: number;
  y: number;
  r: Turn;
}

export function toFurni(p: Omit<Placed, "u">): Furni | null {
  const d = FURNI_BY_ID[p.id];
  if (!d) return null;
  // wall pieces: r picks the wall, and the piece sits on that wall's line
  if (d.wall) return p.r & 1 ? { kind: d.kind, x: 0, y: p.y, w: 1, h: d.w, color: d.color, solid: false, wall: 1 } : { kind: d.kind, x: p.x, y: 0, w: d.w, h: 1, color: d.color, solid: false, wall: 0 };
  const [w, h] = p.r & 1 ? [d.h, d.w] : [d.w, d.h];
  return { kind: d.kind, x: p.x, y: p.y, w, h, color: d.color, solid: d.solid, dir: p.r };
}

/** Tiles covered by solid furniture, as y * MAX_GRID + x. */
export function blockedSet(furni: Placed[]): Set<number> {
  const s = new Set<number>();
  for (const p of furni) {
    const f = toFurni(p);
    if (!f?.solid) continue;
    for (let dx = 0; dx < f.w; dx++) for (let dy = 0; dy < f.h; dy++) s.add((f.y + dy) * MAX_GRID + f.x + dx);
  }
  return s;
}

/** Whether a piece fits: inside the room, solid pieces don't overlap each other or block the door. */
export function canPlace(room: { size: number; furni: Placed[] }, p: Omit<Placed, "u">, ignoreU = -1): boolean {
  const f = toFurni(p);
  if (!f || f.x < 0 || f.y < 0 || f.x + f.w > room.size || f.y + f.h > room.size) return false;
  if (f.wall !== undefined) {
    // wall pieces can't overlap other pieces on the same wall
    const span = (g: Furni) => (g.wall ? [g.y, g.y + g.h] : [g.x, g.x + g.w]);
    const [a0, a1] = span(f);
    return !room.furni.some((q) => {
      if (q.u === ignoreU) return false;
      const g = toFurni(q);
      if (!g || g.wall !== f.wall) return false;
      const [b0, b1] = span(g);
      return a0 < b1 && b0 < a1;
    });
  }
  if (!f.solid) return true;
  const door = doorOf(room.size);
  const taken = blockedSet(room.furni.filter((q) => q.u !== ignoreU));
  for (let dx = 0; dx < f.w; dx++)
    for (let dy = 0; dy < f.h; dy++) {
      const x = f.x + dx, y = f.y + dy;
      if (taken.has(y * MAX_GRID + x) || (x === door.x && y === door.y)) return false;
    }
  return true;
}

const layout = (rows: [string, number, number, Turn][]): Placed[] => rows.map(([id, x, y, r], u) => ({ u, id, x, y, r }));

/** What every new room starts with: just a mattress on the floor. */
export const STARTER_LAYOUT = layout([["mattress", 0, 0, 0]]);

/** Layouts of the old pick-a-theme rooms, so rooms made before personal rooms keep their look. */
export const LEGACY_LAYOUTS: Record<ThemeId, Placed[]> = {
  lounge: layout([
    ["rug_red", 4, 4, 0], ["sofa_blue", 4, 1, 0], ["table_coffee", 5, 5, 0], ["plant_fern", 0, 0, 0], ["plant_fern", 11, 0, 0],
    ["lamp_warm", 8, 1, 0], ["sofa_blue", 1, 6, 1], ["tv_retro", 10, 0, 0], ["beanbag_pink", 9, 4, 0], ["lava_pink", 3, 0, 0],
  ]),
  rooftop: layout([
    ["rug_grey", 3, 3, 0], ["crate_wood", 0, 0, 0], ["plant_lime", 11, 3, 0], ["plant_lime", 11, 7, 0], ["table_patio", 5, 5, 0],
    ["lamp_warm", 3, 9, 0], ["lamp_warm", 8, 9, 0], ["beanbag_blue", 1, 4, 0], ["beanbag_lilac", 1, 6, 0],
  ]),
  arcade: layout([
    ["cabinet_pink", 1, 0, 0], ["cabinet_cyan", 3, 0, 0], ["cabinet_lime", 5, 0, 0], ["cabinet_gold", 0, 3, 0], ["cabinet_purple", 0, 5, 0],
    ["neon_pink", 4, 4, 0], ["loveseat_cyan", 9, 9, 0], ["lava_lime", 11, 8, 0],
  ]),
};

/**
 * Windows, posters and signs the old room styles had painted on their walls. Rooms from before walls were
 * plain wallpaper get them as real wall pieces, so they look the same. Pieces that don't fit a small room
 * are left off or moved closer to the corner, as before.
 */
export function legacyWallPieces(theme: ThemeId, size: number): Omit<Placed, "u">[] {
  const back = (id: string, x: number) => ({ id, x, y: 0, r: 0 as const });
  const left = (id: string, y: number) => ({ id, x: 0, y, r: 1 as const });
  if (theme === "lounge") return [left("window_cloud", 3), back("poster_star", 1), ...(size >= 8 ? [back("poster_heart", 6)] : [])];
  if (theme === "rooftop") return (size >= 10 ? [2, 7] : [1]).map((x) => back("window_sunset", x));
  return [back("sign_neon", size >= 12 ? 7 : 1), left("poster_stripes", size >= 10 ? 7 : 2)];
}

// ---------- wire format ----------

export interface RoomState {
  id: string;
  name: string;
  owner: string; // owner's display name
  size: number;
  wall: string; // WALLS id
  floor: string; // FLOORS id
  furni: Placed[];
  card: string; // CARD_BGS id
  access: Access;
  likes: number;
}

export interface PublicPlayer {
  id: string;
  name: string;
  look: Look;
  x: number;
  y: number;
  dir: number; // 0..3: +x, +y, -x, -y
  xid?: string; // X user id, for players signed in with X (only they can join a Crew)
  home?: string; // id of their own room
}

/** Someone in your Crew, or someone with a Crew request waiting. `id` is their X user id. */
export interface CrewPerson {
  id: string;
  name: string; // "@handle"
  look: Look;
  home?: string; // their own room
  room?: string; // the room they're in right now, if they're online
  roomName?: string;
  giftAt?: number; // when you can start sending them gifts (left out once you can)
}

/** Your Crew: friends both ways, requests to you (`incoming`) and requests you sent (`outgoing`). */
export interface CrewState {
  crew: CrewPerson[];
  incoming: CrewPerson[];
  outgoing: CrewPerson[];
}

export const MAX_CREW = 300;

// ---------- gifts ----------

/**
 * Gifts are Shop items bought for someone in your Crew. Bits themselves can't be sent: that keeps free
 * daily Bits from being farmed on spare accounts, and keeps Bits bought with a card from being passed on.
 */
export type GiftKind = "furni" | "clothes" | "hair" | "emote" | "wall" | "floor";
export const GIFT_KINDS: GiftKind[] = ["furni", "clothes", "hair", "emote", "wall", "floor"];
export const GIFTS_PER_DAY = 3; // gifts one player can send in any 24 hours
export const GIFT_CREW_MS = 3 * 86400_000; // how long two players must be Crew before gifting each other
export const MAX_GIFT_NOTE = 80;

/** A gift waiting to be unwrapped. The item is already theirs; this is the wrapping and the note. */
export interface Gift {
  id: string;
  from: string; // the sender's @handle
  look: Look; // the sender, as they looked when they sent it
  kind: GiftKind;
  item: string; // id in its catalog
  note?: string;
  at: number;
}

// Client -> server
export type ClientMsg =
  | { t: "hello"; token: string; name?: string; sid?: string }
  | { t: "move"; x: number; y: number }
  | { t: "chat"; text: string }
  | { t: "emote"; e: string } // an emote id you own
  | { t: "buyEmote"; id: string }
  | { t: "look"; look: Look }
  | { t: "buy"; item: string }
  | { t: "daily" }
  | { t: "buyFurni"; id: string } // goes to your inventory, from any room
  | { t: "buyHair"; id: string } // a HAIRS id
  | { t: "buyWall"; id: string }
  | { t: "buyFloor"; id: string }
  // room owner only
  | { t: "place"; id: string; x: number; y: number; r: Turn } // from inventory
  | { t: "moveFurni"; u: number; x: number; y: number; r: Turn }
  | { t: "pickup"; u: number } // back to inventory
  | { t: "expand" }
  | { t: "wall"; id: string } // one you own
  | { t: "floor"; id: string }
  | { t: "roomName"; name: string }
  | { t: "cardBg"; id: string }
  | { t: "access"; access: Access }
  | { t: "answer"; kid: string; yes: boolean } // let someone who knocked in, or not
  // anyone signed in with X, in someone else's room
  | { t: "like" } // likes the room, or takes the like back
  // Crew, signed in with X only. People are named by X user id, or by a player in the room (pid) or @handle when adding.
  | { t: "crewAdd"; id?: string; pid?: string; handle?: string }
  | { t: "crewAccept"; id: string }
  | { t: "crewIgnore"; id: string }
  | { t: "crewRemove"; id: string } // also takes back a request you sent
  // gifts: buy a Shop item for someone in your Crew (by X user id), and unwrap ones you got
  | { t: "gift"; to: string; kind: GiftKind; id: string; note?: string }
  | { t: "openGift"; id: string };

export interface Profile {
  name: string; // "@handle" when signed in with X, otherwise a guest name like "guest4821"
  x?: { id: string; handle: string }; // the X account, once signed in
  bits: number;
  owned: string[];
  look: Look;
  dailyReadyAt: number;
  home?: string; // id of this player's own room
  inv: Record<string, number>; // furniture bought but not placed, by furni id
  emotes: string[]; // emotes bought (free ones are not listed)
  hairs: string[]; // hairstyles owned, by HAIRS id (free ones are not listed)
  walls: string[]; // walls and floors owned (free ones are not listed)
  floors: string[];
  // Crew, by profile key ("x:<X user id>"); signed-in players only
  crew?: string[]; // friends both ways
  crewIn?: string[]; // requests waiting for this player's answer
  crewOut?: string[]; // requests this player sent that are still open
  crewSince?: Record<string, number>; // when each Crew member joined (missing for Crews from before gifts)
  gifts?: Gift[]; // gifts waiting to be unwrapped
  giftsSent?: number[]; // when this player sent gifts in the last 24 hours
  seen?: number; // start of the last day (UTC) this player was around; idle guests get cleaned up
}

// Server -> client
export type ServerMsg =
  | {
      t: "welcome";
      you: string;
      room: RoomState;
      mine: boolean; // you own this room
      liked: boolean; // you like this room
      players: PublicPlayer[];
      profile: Profile;
    }
  | { t: "room"; room: RoomState }
  | { t: "full"; room: RoomState; people: number } // sent before closing: the room has as many people as its size allows
  | { t: "liked"; liked: boolean }
  // locked rooms: sent before closing. why: "crew" (Crew only), "away" (nobody to open the door), "no" (not let in)
  | { t: "locked"; room: RoomState; why: "crew" | "away" | "no" }
  | { t: "knocking"; room: RoomState } // waiting for the owner to answer
  | { t: "knock"; kid: string; name: string; look: Look } // to the owner: someone's at the door
  | { t: "knockGone"; kid: string } // they stopped waiting, or someone else answered
  | { t: "join"; p: PublicPlayer }
  | { t: "leave"; id: string }
  | { t: "pos"; moves: [id: string, x: number, y: number, dir: number][] }
  | { t: "chat"; id: string; text: string }
  | { t: "emote"; id: string; e: string }
  | { t: "look"; id: string; look: Look; name: string }
  | { t: "profile"; profile: Profile }
  | { t: "crew"; crew: CrewState; news?: string } // news: something to tell the player, like a new request
  | { t: "gifted"; msg: string } // your gift went out
  | { t: "error"; msg: string };
